import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import investorPortalApi from "@/lib/investorPortalApi";
import { useInvestorPortalAuth } from "@/contexts/InvestorPortalAuthContext";

const Section = ({ title, eyebrow, accent = "sky", children, testId }) => (
  <motion.section
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.05 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
    data-testid={testId}
  >
    <div className={`absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl ${
      accent === "sky" ? "bg-skyMist" : accent === "sage" ? "bg-sageMint" : accent === "rose" ? "bg-roseMilk" : "bg-butterCream"
    }`} aria-hidden />
    {eyebrow && <p className="wit-eyebrow text-raspberry mb-2">{eyebrow}</p>}
    <h2 className="font-serif text-xl text-cocoa mb-5">{title}</h2>
    {children}
  </motion.section>
);

const Field = ({ label, children, testId }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <label className="text-xs font-medium text-warmGrey uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, type = "text", placeholder, autoComplete, testId }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoComplete={autoComplete}
    className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa transition-colors"
    data-testid={testId}
  />
);

export default function InvestorProfile() {
  const { t } = useTranslation();
  const { session, refreshSession } = useInvestorPortalAuth();
  const me = session?.me || {};
  const personal = me.personal || {};
  const org = me.organization || {};
  const comms = me.communications || {};

  const [form, setForm] = useState({
    personal: { ...personal },
    organization: { ...org },
    communications: { ...comms },
  });
  const [saving, setSaving] = useState(null);

  const upd = (section, key, val) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: val } }));

  const save = async (sectionKey, payload) => {
    setSaving(sectionKey);
    try {
      await investorPortalApi.patch("/me", { [sectionKey]: payload });
      await refreshSession();
      toast.success(t("invPortal.profile.saved"));
    } catch (_e) {
      toast.error(t("invPortal.profile.saveFailed"));
    } finally {
      setSaving(null);
    }
  };

  const renderSaveBar = (sectionKey, payload) => (
    <div className="flex items-center justify-end gap-2 mt-5 pt-5 border-t border-sandBeige">
      <button
        type="button"
        onClick={() => save(sectionKey, payload)}
        disabled={saving === sectionKey}
        className="bg-raspberry hover:bg-coral disabled:opacity-60 text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
        data-testid={`inv-save-${sectionKey}`}
      >
        {saving === sectionKey ? (
          <><Loader2 size={14} className="animate-spin" /> {t("invPortal.profile.saving")}</>
        ) : (
          <><Check size={14} /> {t("invPortal.profile.save")}</>
        )}
      </button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="investor-portal-profile">
      <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.profile.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">{t("invPortal.profile.title")}</h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.profile.lead")}</p>
      </motion.header>

      {/* Personal */}
      <Section title={t("invPortal.profile.personalTitle")} eyebrow={t("invPortal.profile.personalEyebrow")} accent="sky" testId="inv-section-personal">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("invPortal.fields.first_name")}>
            <TextInput value={form.personal.first_name} onChange={(v) => upd("personal", "first_name", v)} autoComplete="given-name" testId="inv-input-firstname" />
          </Field>
          <Field label={t("invPortal.fields.last_name")}>
            <TextInput value={form.personal.last_name} onChange={(v) => upd("personal", "last_name", v)} autoComplete="family-name" testId="inv-input-lastname" />
          </Field>
          <Field label={t("invPortal.fields.email")}>
            <div className="bg-ivory border border-sandBeige rounded-2xl px-4 py-3 text-sm text-cocoa flex items-center gap-2" data-testid="inv-locked-email">
              <span className="flex-1 truncate">{personal.email}</span>
              <span className="text-[10px] uppercase text-warmGrey tracking-wide">{t("invPortal.profile.locked")}</span>
            </div>
          </Field>
          <Field label={t("invPortal.fields.phone")}>
            <TextInput value={form.personal.phone} onChange={(v) => upd("personal", "phone", v)} type="tel" autoComplete="tel" testId="inv-input-phone" />
          </Field>
          <div className="sm:col-span-2">
            <Field label={t("invPortal.fields.linkedin")}>
              <TextInput value={form.personal.linkedin} onChange={(v) => upd("personal", "linkedin", v)} type="url" placeholder="https://linkedin.com/in/…" testId="inv-input-linkedin" />
            </Field>
          </div>
        </div>
        {renderSaveBar("personal", form.personal)}
      </Section>

      {/* Organization */}
      <Section title={t("invPortal.profile.orgTitle")} eyebrow={t("invPortal.profile.orgEyebrow")} accent="sage" testId="inv-section-organization">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("invPortal.fields.organization")}>
            <TextInput value={form.organization.name} onChange={(v) => upd("organization", "name", v)} testId="inv-input-org-name" />
          </Field>
          <Field label={t("invPortal.fields.org_website")}>
            <TextInput value={form.organization.website} onChange={(v) => upd("organization", "website", v)} type="url" placeholder="https://" testId="inv-input-org-website" />
          </Field>
          <Field label={t("invPortal.fields.country")}>
            <TextInput value={form.organization.country} onChange={(v) => upd("organization", "country", v)} testId="inv-input-country" />
          </Field>
          <Field label={t("invPortal.fields.city")}>
            <TextInput value={form.organization.city} onChange={(v) => upd("organization", "city", v)} testId="inv-input-city" />
          </Field>
        </div>
        {renderSaveBar("organization", form.organization)}
      </Section>

      {/* Communications */}
      <Section title={t("invPortal.profile.commsTitle")} eyebrow={t("invPortal.profile.commsEyebrow")} accent="butter" testId="inv-section-communications">
        <div className="flex flex-col gap-4">
          <Field label={t("invPortal.fields.comms_preference")}>
            <select
              value={form.communications.preference || "portal_email"}
              onChange={(e) => upd("communications", "preference", e.target.value)}
              className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 focus:border-raspberry focus:outline-none text-sm text-cocoa transition-colors"
              data-testid="inv-input-comms-pref"
            >
              <option value="portal_only">{t("invPortal.profile.commsPortalOnly")}</option>
              <option value="portal_email">{t("invPortal.profile.commsPortalEmail")}</option>
              <option value="email_only">{t("invPortal.profile.commsEmailOnly")}</option>
            </select>
          </Field>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="inv-input-monthly-digest">
            <input
              type="checkbox"
              checked={form.communications.monthly_digest !== false}
              onChange={(e) => upd("communications", "monthly_digest", e.target.checked)}
              className="w-5 h-5 rounded border-sandBeige text-raspberry focus:ring-raspberry"
            />
            <span className="text-sm text-cocoa">{t("invPortal.profile.commsMonthly")}</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="inv-input-immediate-alerts">
            <input
              type="checkbox"
              checked={form.communications.immediate_alerts !== false}
              onChange={(e) => upd("communications", "immediate_alerts", e.target.checked)}
              className="w-5 h-5 rounded border-sandBeige text-raspberry focus:ring-raspberry"
            />
            <span className="text-sm text-cocoa">{t("invPortal.profile.commsImmediate")}</span>
          </label>
        </div>
        {renderSaveBar("communications", form.communications)}
      </Section>
    </div>
  );
}
